import React, { useState } from 'react';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import testFirestore from '../testFirestore';

const FirebaseTest = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test, status, message, details = null) => {
    setResults(prev => [...prev, { test, status, message, details, timestamp: new Date() }]);
  };

  const testFirebaseAuth = async () => {
    setLoading(true);
    setResults([]);

    // Test 1: Check if auth is initialized
    try {
      if (auth) {
        addResult('Auth Initialization', '✅', 'Firebase Auth is initialized');
      } else {
        addResult('Auth Initialization', '❌', 'Firebase Auth is not initialized');
        return;
      }
    } catch (error) {
      addResult('Auth Initialization', '❌', 'Error checking auth', error.message);
      return;
    }

    // Test 2: Check current user
    try {
      const user = auth.currentUser;
      if (user) {
        addResult('Current User', '✅', `User logged in: ${user.email}`);
      } else {
        addResult('Current User', 'ℹ️', 'No user currently logged in');
      }
    } catch (error) {
      addResult('Current User', '❌', 'Error checking current user', error.message);
    }

    // Test 3: Test Firestore
    try {
      const firestoreWorks = await testFirestore();
      if (firestoreWorks) {
        addResult('Firestore Test', '✅', 'Firestore is working correctly');
      } else {
        addResult('Firestore Test', '❌', 'Firestore test failed');
      }
    } catch (error) {
      addResult('Firestore Test', '❌', 'Firestore error', error.message);
    }

    // Test 4: Try to create a test user
    try {
      const testEmail = `test${Date.now()}@example.com`;
      const testPassword = 'test123456';
      
      const result = await createUserWithEmailAndPassword(auth, testEmail, testPassword);
      addResult('Create User', '✅', 'Successfully created test user', `UID: ${result.user.uid}`);
      
      // Clean up - delete the test user
      await result.user.delete();
      addResult('Cleanup', '✅', 'Test user deleted successfully');
      
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        addResult('Create User', '✅', 'Auth working (email exists is expected)', error.message);
      } else if (error.code === 'auth/configuration-not-found') {
        addResult('Create User', '❌', 'Email/Password auth NOT enabled', 'Enable in Firebase Console > Authentication > Sign-in method');
      } else {
        addResult('Create User', '❌', 'Failed to create user', `${error.code}: ${error.message}`);
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Firebase Authentication Test</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <button
            onClick={testFirebaseAuth}
            disabled={loading}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Run Firebase Auth Tests'}
          </button>
        </div>

        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h2>
            <div className="space-y-3">
              {results.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg border ${
                  result.status.includes('✅') ? 'bg-green-50 border-green-200' :
                  result.status.includes('❌') ? 'bg-red-50 border-red-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {result.status} {result.test}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {result.message}
                      </div>
                      {result.details && (
                        <div className="text-xs text-gray-500 mt-2 font-mono bg-gray-100 p-2 rounded">
                          {result.details}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {result.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">🔧 If tests fail, check:</h3>
          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
            <li>Firebase Console → Authentication → Sign-in method</li>
            <li>Enable "Email/Password" authentication</li>
            <li>Check that your Firebase project ID matches: "groupexptracker"</li>
            <li>Verify API key is correct and not restricted</li>
            <li>Firebase Console → Firestore Database → Get Started</li>
            <li>Check Firestore security rules (allow read/write for testing)</li>
            <li>Check network connectivity</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default FirebaseTest;
